import { formatPhp } from "../../lib/money";
import { type PurchaseStatusFilter } from "../../lib/purchaseStatus";
import { type ProjectAgreement } from "../../lib/api";

export const peso = formatPhp;

export function offerDeadlineNote(iso: string | null | undefined): { text: string; expired: boolean } | null {
  if (!iso) return null;
  const d = new Date(iso);
  const ms = d.getTime() - Date.now();
  if (ms <= 0) return { text: "This offer has expired.", expired: true };
  const days = Math.ceil(ms / 86400000);
  return { text: `Expires ${d.toLocaleDateString("en-PH", { month: "short", day: "numeric" })} · ${days} day${days === 1 ? "" : "s"} left to accept`, expired: false };
}

export type ContractStatusFilter = "all" | "offer" | "signature" | "funding" | "active" | "disputed" | "completed" | "cancelled";

export type ListStatusFilter = ContractStatusFilter | PurchaseStatusFilter;

export type ContractTab = "overview" | "milestones" | "work" | "payment" | "agreement" | "activity";

export type DateSort = "newest" | "oldest" | null;

export type PriceSort = "highest" | "lowest" | null;

export type Milestone = {
  id: string;
  label: string;
  amount: number;
  status: "pending" | "released" | "cancelled";
  releasedAt?: string | null;
};

export type Contract = {
  negotiationId?: string;
  agreement?: ProjectAgreement;
  agreementProposalId?: string | null;
  agreementDocument?: import("../../lib/api").AgreementDocument | null;
  id: string;
  sourceType: string;
  sourceId: string;
  sharedWork?: boolean;
  rfqId?: string | null;
  threadId?: string | null;
  title: string;
  scopeOfWork?: string | null;
  proposedCompletionDate?: string | null;
  buyerSignature?: string | null;
  buyerSignedAt?: string | null;
  providerSignature?: string | null;
  providerSignedAt?: string | null;
  amount: number;
  releasedAmount?: number;
  status: "pending_funding" | "funded" | "disputed" | "completed" | "cancelled";
  offerStatus: "pending" | "accepted" | "declined";
  offerDeadline?: string | null;
  workerRespondedAt?: string | null;
  role: "buyer" | "provider";
  counterparty?: string | null;
  milestones: Milestone[];
  createdAt: string;
};

/** Job workspaces share presentation, while their transport and retained data
 * stay engagement-owned; sharedWork continues to classify the contract API. */
export const sharedContractWork = (contract: Contract) => contract.sourceType === "job" || (contract.sharedWork ?? ["project", "service", "hire", "business", "rfq_items"].includes(contract.sourceType));

export const STATUS_FILTERS: Array<{ key: ContractStatusFilter; label: string }> = [
  { key: "all", label: "contracts.filter.all" },
  { key: "offer", label: "contracts.filter.offer" },
  { key: "signature", label: "contracts.filter.signature" },
  { key: "funding", label: "contracts.filter.funding" },
  { key: "active", label: "contracts.filter.active" },
  { key: "disputed", label: "Disputed" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];

// One pending_funding database state spans offer, signature, and funding work.
// Derive the actionable phase from the source and signatures for list filters.
export function contractStatusKey(contract: Contract): Exclude<ContractStatusFilter, "all"> {
  if (contract.status === "pending_funding") {
    if (contract.offerStatus === "pending") return "offer";
    if ((contract.sourceType === "project" || contract.sourceType === "service" || contract.sourceType === "business" || contract.sourceType === "rfq_items" || contract.sourceType === "job") && contract.offerStatus === "accepted" && (!contract.providerSignedAt || !contract.buyerSignedAt)) return "signature";
    return "funding";
  }
  if (contract.status === "funded") return "active";
  return contract.status;
}

export function contractStatusLabel(contract: Contract, view: "list" | "detail" = "detail", t?: (key: string) => string): string {
  if (contract.negotiationId) return t ? t("projectAgreement.negotiation") : "Agreement negotiation";
  if (contract.status === "cancelled" && contract.offerStatus === "declined" && contract.workerRespondedAt) return "Offer declined";
  if (t && (contract.status === "completed" || contract.status === "cancelled")) return t(`transaction.status.${contract.status}`);
  if (contract.sourceType === "order" && contract.status === "funded") return "Paid";
  if (contractStatusKey(contract) === "signature") {
    if (view === "list") return "Pending signature";
    if (contract.sourceType === "rfq_items") {
      const signer = !contract.providerSignedAt ? "provider" : "buyer";
      if (contract.role === signer) return "Pending for your signature";
      return signer === "provider" ? "Pending seller signature" : "Pending buyer signature";
    }
    return (contract.role === "buyer" ? contract.buyerSignedAt : contract.providerSignedAt) ? "Waiting for the other signature" : "Pending for your signature";
  }
  const label = STATUS_FILTERS.find(({ key }) => key === contractStatusKey(contract))?.label ?? contract.status;
  return t && label.startsWith("contracts.filter.") ? t(label) : label;
}

export function statusChipClass(status: Exclude<ContractStatusFilter, "all">): string {
  const classes: Record<Exclude<ContractStatusFilter, "all">, string> = {
    offer: "bg-brand-gold/10 text-[#8a5a00]",
    signature: "bg-orange-50 text-orange-700",
    funding: "bg-orange-50 text-orange-700",
    active: "bg-sky-50 text-sky-700",
    disputed: "bg-brand-red/10 text-brand-red",
    completed: "bg-brand-green/10 text-brand-greendark",
    cancelled: "bg-navy-50 text-mut",
  };
  return classes[status];
}

export function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function agreementWorkspaceContract(agreement: ProjectAgreement): Contract {
  return { id: agreement.contractId || agreement.negotiationId, negotiationId: agreement.negotiationId, agreement, agreementDocument: agreement.document,
    sourceType: "project", sourceId: agreement.projectId, threadId: agreement.threadId, title: agreement.title,
    scopeOfWork: agreement.scopeOfWork, proposedCompletionDate: agreement.proposedCompletionDate,
    amount: agreement.amount, status: "pending_funding", offerStatus: agreement.offerStatus || "pending", role: agreement.role,
    counterparty: agreement.role === "buyer" ? agreement.providerName : agreement.buyerName,
    milestones: agreement.milestones.map((milestone, index) => ({ ...milestone, id: `${agreement.id}-${index}`, status: "pending" })),
    createdAt: agreement.createdAt };
}

export type ContractWorkspaceProps = {
  contract: Contract;
  isMobile: boolean;
  activeTab: ContractTab;
  busy: string | null;
  error: string | null;
  onBack: () => void;
  onTabChange: (tab: ContractTab) => void;
  onFund: () => void;
  onRelease: (milestoneId: string) => void;
  onAccept: (signatureName?: string) => void;
  onDecline: () => void;
  onWithdraw: () => void;
  onDownload: (kind: "contract" | "receipt") => void;
  onReload: () => void;
  onNegotiate: () => void;
};
